import type { FAQItem } from "~/lib/price-ranges";
import {
  MINOR_CLIENT_AGE_RANGE_MINIMUM_AGES,
  type MinorClientAgeRangeMinimum,
} from "~/lib/minor-age";

export type ClientAgeRangeSlug = `od-${MinorClientAgeRangeMinimum}-godina`;

export type ClientAgeRangeData = {
  label: string;
  maxMinimumClientAge: number;
  description: string;
  seoTitle: string;
  seoDescription: string;
  faqItems: FAQItem[];
};

function getAgeRangeFaqItems(age: number): FAQItem[] {
  return [
    {
      question: `Kako biram terapeuta za klijenta od ${age} godina?`,
      answer:
        "Pogledaj uzrast sa kojim terapeut radi, oblasti za koje je specijalizovan, iskustvo i način rada. Ako nisi siguran koji terapeut odgovara, možeš krenuti od upitnika za preporuku.",
    },
    {
      question: "Da li je potreban pristanak roditelja ili staratelja?",
      answer:
        "Za maloletne klijente potreban je pristanak roditelja ili staratelja pre početka terapije. Platforma će prikazati korake kada uneseš datum rođenja klijenta.",
    },
    {
      question: "Da li maloletni klijent može da ima online terapiju?",
      answer:
        "Da, ako terapeut radi sa tim uzrastom i postoje potrebni uslovi za bezbedan i poverljiv razgovor. Online format često olakšava uklapanje termina sa školom i porodičnim obavezama.",
    },
    {
      question: "Za koje probleme mlađi klijenti najčešće traže podršku?",
      answer:
        "Najčešći razlozi su anksioznost, stres, školski pritisak, odnosi u porodici, samopouzdanje, tuga, promene raspoloženja i teškoće u odnosima sa vršnjacima.",
    },
  ];
}

function createClientAgeRange(age: MinorClientAgeRangeMinimum) {
  return {
    label: `Terapeuti za ${age}+`,
    maxMinimumClientAge: age,
    description: `Pronađi psihologe i psihoterapeute koji rade sa klijentima od ${age} godina. Pogledaj profile terapeuta, oblasti rada, cenu sesije i prvi slobodan termin.`,
    seoTitle: `Terapeuti za ${age}+ | Online psihoterapija`,
    seoDescription: `Online terapija za klijente od ${age} godina sa terapeutima koji rade sa mlađim klijentima. Uporedi profile, cene i slobodne termine.`,
    faqItems: getAgeRangeFaqItems(age),
  };
}

export const CLIENT_AGE_RANGES = Object.fromEntries(
  MINOR_CLIENT_AGE_RANGE_MINIMUM_AGES.map((age) => [
    `od-${age}-godina`,
    createClientAgeRange(age),
  ]),
) as Record<ClientAgeRangeSlug, ClientAgeRangeData>;

export function getAllClientAgeRangeSlugs() {
  return Object.keys(CLIENT_AGE_RANGES) as ClientAgeRangeSlug[];
}

export function getClientAgeRangeBySlug(slug: string) {
  return CLIENT_AGE_RANGES[slug as ClientAgeRangeSlug];
}

export function getFAQItemsForClientAgeRange(slug: string) {
  return getClientAgeRangeBySlug(slug)?.faqItems ?? [];
}
